/**
 * @fileoverview Edit Client mutation hook.
 * @description Saves a client's sections with its `tp1.` ETag as If-Match (PATCH
 * /sales/clients/{tenantId}) and refreshes every sales query once it succeeds. The ETag is the one the
 * dialog loaded and showed, so a client that changed since is refused (`412 / 4105`) and shown for
 * review rather than overwritten. The command's idempotency key outlives the dialog, so a retry after
 * an unknown outcome replays it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { editClient } from '@/features/sales/services/sales-clients.service'

import type {
  EditClientRequest,
  VersionedClientDetails,
} from '@/features/sales/types/sales-client.types'

/** The input of one edit command. */
export interface EditClientInput {
  tenantId: string
  etag: string
  request: EditClientRequest
}

/**
 * @description Edits a client.
 * @param {(result: VersionedClientDetails) => void} [onSuccess] - Called after the edit is saved,
 * even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the client, the ETag
 * the dialog loaded and showed, and the request).
 */
export function useEditClient(onSuccess?: (result: VersionedClientDetails) => void) {
  return useIdempotentMutation<EditClientInput, VersionedClientDetails>({
    action: 'edit-client',
    /**
     * @description Scopes the key to the client and the edit made, not the version it is sent against.
     * @param {EditClientInput} input - The edit's input.
     * @returns {object} The client id and the edit.
     */
    scopeOf: ({ tenantId, request }) => ({ tenantId, request }),
    /**
     * @description Sends the edit under the key it is given.
     * @param {EditClientInput} input - The client, its version and the edit.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<VersionedClientDetails>} The client as saved.
     */
    send: ({ tenantId, etag, request }, key) => editClient(tenantId, etag, request, key),
    onSuccess,
  })
}
